import {TFunction} from "i18next";

/**
 * The Static Texts used in this application.
 */
class Texts {


    // ########################### HTTP SPECIFIC API RESPONSE ###################################

    /**
     * When the given request is incomplete or wrong in the context of the server call
     */
    static badRequest(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.badRequest, {ns: '@bizkeytech'});
    }

    /**
     * When the user should log in to access the data from the server.
     */
    static unauthorized(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.unauthorized, {ns: '@bizkeytech'});
    }

    /**
     * When the user does not have access to the requested data.
     */
    static forbiddenAccess(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.forbiddenAccess, {ns: '@bizkeytech'});
    }

    /**
     * When server was not found to communicate with
     */
    static notFound(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.notFound, {ns: '@bizkeytech'});
    }

    /**
     * When server instance for the api call crashed or was terminated before any response could be given
     */
    static serverError(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.serverError, {ns: "@bizkeytech"});
    }

    // ########################### CUSTOM API RESPONSE ###################################

    /**
     * When the server call failed due to a problem in request preparation.
     */
    static requestFailed(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.requestFailed, {ns: "@bizkeytech"});
    }

    /**
     * When the server call successfully was made but response was never returned.
     */
    static serverNotResponded(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.serverNotResponded, {ns: "@bizkeytech"})
    }

    /**
     * When the server call failed due to entity not found. (Internal status code)
     */
    static internalNotFound(t: TFunction<'@bizkeytech'>) {
        return t(t => t.base.core.texts.internalNotFound, {ns: "@bizkeytech"})
    }


    // ########################### FORM VALIDATION ###################################

    /**
     * The texts used in form validation.
     */
    static readonly FormValidation = class {

        /**
         * The text to show when a field is required.
         */
        static requiredField(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid field.
         */
        static invalidField(t: TFunction<'@bizkeytech'>, field: string) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", field});
        };

        /**
         * The text to show when a field is not a valid email.
         */
        static invalidEmail(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid phone number.
         */
        static invalidPhoneNumber(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid URL.
         */
        static invalidUrl(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid number.
         */
        static invalidNumber(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid date.
         */
        static invalidDate(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid time.
         */
        static invalidTime(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show when a field is not a valid date time.
         */
        static afterDate(t: TFunction<'@bizkeytech'>, date: string) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", date});
        };

        /**
         * The text to show when a field is not a valid date time.
         */
        static beforeDate(t: TFunction<'@bizkeytech'>, date: string) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", date});
        };

        /**
         * The text to show the minimum length of the field.
         */
        static minLength(t: TFunction<'@bizkeytech'>, length: number) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", length});
        };

        /**
         * The text to show the maximum length of the field.
         */
        static maxLength(t: TFunction<'@bizkeytech'>, length: number) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", length});
        };

        /**
         * The text to show the minimum value of the field.
         */
        static minNumber(t: TFunction<'@bizkeytech'>, number: number) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", number});
        };

        /**
         * The text to show a positive number.
         */
        static positiveNumber(t: TFunction<'@bizkeytech'>) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech",});
        };

        /**
         * The text to show the maximum value of the field.
         */
        static maxNumber(t: TFunction<'@bizkeytech'>, number: number) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", number});
        };

        /**
         * The text to show the minimum array length.
         */
        static minArrayLength(t: TFunction<'@bizkeytech'>, length: number) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", length});
        };

        /**
         * The text to show the maximum array length.
         */
        static maxArrayLength(t: TFunction<'@bizkeytech'>, length: number) {
            return t(t => t.base.core.formValidation, {ns: "@bizkeytech", length});
        };
    }
}

export default Texts;
